import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MaterialIcon from '../ui/MaterialIcon'
import { KILO } from '../../constants/KILO_THEME'
import {
  submitMorningWellness,
  type WellnessPillars,
} from '../../lib/submitMorningWellness'
import type { AthleteCompanionPayload } from '../../lib/dailyOverrideTypes'

type PillarKey = keyof WellnessPillars

const PILLAR_ORDER: PillarKey[] = [
  'stress',
  'fatigue',
  'sleep_quality',
  'muscle_soreness',
  'mood',
]

type MorningCheckInProps = {
  onSubmitted?: (companion: AthleteCompanionPayload | null) => void
  onPillarsComplete?: (pillars: WellnessPillars) => void
  embedded?: boolean
  deferDispatch?: boolean
  dispatching?: boolean
  dispatchError?: string | null
  /** ISO timestamp for the session-only success banner. */
  sessionCompletedAt?: string | null
  /** When true, render the compact success row (no wizard). */
  showSessionBanner?: boolean
}

const MorningCheckIn = ({
  onSubmitted,
  onPillarsComplete,
  embedded = false,
  deferDispatch = false,
  dispatching = false,
  dispatchError = null,
  sessionCompletedAt = null,
  showSessionBanner = false,
}: MorningCheckInProps) => {
  const { t, i18n } = useTranslation()
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<Partial<WellnessPillars>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [wizardComplete, setWizardComplete] = useState(showSessionBanner)
  const [completedAt, setCompletedAt] = useState<string | null>(sessionCompletedAt)
  const [slideKey, setSlideKey] = useState(0)

  useEffect(() => {
    if (sessionCompletedAt) setCompletedAt(sessionCompletedAt)
    if (showSessionBanner) setWizardComplete(true)
  }, [sessionCompletedAt, showSessionBanner])

  const currentPillar = PILLAR_ORDER[step]
  const totalSteps = PILLAR_ORDER.length
  const progressPct = wizardComplete ? 100 : ((step + 1) / totalSteps) * 100

  const optionLabels = useMemo(() => {
    return [1, 2, 3, 4, 5].map((value) =>
      t(`coach.wellness.pillars.${currentPillar}.options.${value}`)
    )
  }, [currentPillar, t])

  const calibratedLabel = useMemo(() => {
    const iso = completedAt ?? sessionCompletedAt
    if (!iso) return null
    const time = new Date(iso).toLocaleTimeString(i18n.language, {
      hour: 'numeric',
      minute: '2-digit',
    })
    return t('coach.wellness.calibratedAt', { time })
  }, [completedAt, sessionCompletedAt, i18n.language, t])

  const finish = useCallback(async (finalAnswers: WellnessPillars) => {
    const stampedAt = new Date().toISOString()
    setCompletedAt(stampedAt)
    setWizardComplete(true)

    if (deferDispatch) {
      onPillarsComplete?.(finalAnswers)
      return
    }

    setBusy(true)
    setError(null)
    const { result, error: submitError } = await submitMorningWellness(finalAnswers)
    setBusy(false)
    if (submitError) {
      setError(submitError)
      setWizardComplete(false)
      return
    }
    onSubmitted?.(result?.athlete_companion ?? null)
  }, [deferDispatch, onPillarsComplete, onSubmitted])

  const selectOption = useCallback((value: number) => {
    if (busy || wizardComplete || dispatching) return

    const pillar = PILLAR_ORDER[step]
    const nextAnswers = { ...answers, [pillar]: value } as Partial<WellnessPillars>

    if (step >= totalSteps - 1) {
      const allKeys = PILLAR_ORDER.every((k) => nextAnswers[k] != null)
      if (!allKeys) return
      void finish(nextAnswers as WellnessPillars)
      return
    }

    setAnswers(nextAnswers)
    setSlideKey((k) => k + 1)
    window.setTimeout(() => setStep((s) => s + 1), 180)
  }, [answers, busy, wizardComplete, dispatching, finish, step, totalSteps])

  const externalError = dispatchError ?? error
  const showBusy = busy || dispatching

  if (wizardComplete || showSessionBanner) {
    return (
      <div className={embedded ? 'py-2 px-4' : 'rounded-xl border border-kilo-border bg-kilo-page py-2 px-4'}>
        <div className="flex items-center gap-2">
          <MaterialIcon name="check_circle" size="sm" className="shrink-0 !text-emerald-500" filled />
          <p className={`text-sm ${KILO.text.muted}`}>{t('coach.wellness.completeTitle')}</p>
          {calibratedLabel ? (
            <p className={`ml-auto shrink-0 text-xs ${KILO.text.soft}`}>{calibratedLabel}</p>
          ) : null}
        </div>
        {externalError ? <p className="mt-1 text-xs text-red-400">{externalError}</p> : null}
      </div>
    )
  }

  const shell = embedded
    ? KILO.embeddedSection
    : 'rounded-3xl border border-kilo-border bg-kilo-page p-5 shadow-kilo-card'

  return (
    <div className={shell}>
      <div className="mb-4">
        <div className="mb-2 flex items-center justify-between">
          <p className={`text-xs font-mono uppercase tracking-widest ${KILO.text.soft}`}>
            {t('coach.wellness.progress', { step: step + 1, total: totalSteps })}
          </p>
          <span className={`text-xs ${KILO.text.muted}`}>{Math.round(progressPct)}%</span>
        </div>
        <div className={KILO.progressTrack}>
          <div
            className="h-full rounded-full bg-kilo-accent transition-all duration-300 ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      <div key={slideKey} className="wellness-card-enter">
        <p className={`text-lg font-bold ${KILO.text.primary}`}>
          {t(`coach.wellness.pillars.${currentPillar}.title`)}
        </p>
        <p className={`mt-1 text-xs ${KILO.text.soft}`}>
          {t('coach.wellness.tapHint')}
        </p>

        <div className="mt-4 flex flex-col gap-2" role="group" aria-label={t(`coach.wellness.pillars.${currentPillar}.title`)}>
          {[1, 2, 3, 4, 5].map((value, idx) => {
            const selected = answers[currentPillar] === value
            return (
              <button
                key={value}
                type="button"
                disabled={showBusy}
                aria-pressed={selected}
                onClick={() => selectOption(value)}
                className={`min-h-12 w-full rounded-xl border px-4 py-3 text-left text-sm font-semibold transition active:scale-[0.98] ${
                  selected
                    ? 'border-kilo-accent bg-kilo-accent/20 text-kilo-text'
                    : 'border-kilo-line bg-kilo-nested text-kilo-text hover:border-kilo-accent/40'
                }`}
              >
                <span className={`mr-2 font-mono text-xs ${KILO.text.soft}`}>{value}</span>
                {optionLabels[idx]}
              </button>
            )
          })}
        </div>
      </div>

      {showBusy ? (
        <p className={`mt-4 text-xs ${KILO.text.soft}`}>{t('coach.wellness.submitting')}</p>
      ) : null}
      {externalError ? <p className="mt-3 text-sm text-red-400">{externalError}</p> : null}
    </div>
  )
}

export default MorningCheckIn
